// src/lib/map-overlay/chipPlacement.js
//
// Where a capture chip goes on the map (footprint restyle S-c2, DB
// 2026-11-03), in the map container's pixels. Pure: the caller projects the
// footprint and measures what floats on the map; CaptureChips.js draws it.
//
// ── THE SAFE AREA ──────────────────────────────────────────────────────────
//
// The map (or, in Compare, its side of the slider: clipX) minus the boxes of
// what floats on it: the HUD's slots, the imagery strip, the credits footer
// and the like. The chip is a rectangle, so the question is where its CENTER
// may sit with the whole rectangle in the safe area, CHIP_GAP_PX clear of
// every box and the map's edge. That region (chipRegion) is a rectangle with
// rectangular holes: each obstacle grown by half the chip and the gap.
//
// ── PLACEMENT (placeChip), in order ────────────────────────────────────────
//
//   kept     the chip's current position, while it is still in the region
//            and on the footprint: a chip never jumps while it can stay
//   label    the footprint's label_point (ST_PointOnSurface), when in the region
//   anchor   the map center inside the footprint (it fills the view): the
//            region's top-center, walked down past anything at the top
//            center, i.e. just below the toolbar slot (fixedAnchor)
//   nearest  else the point of the region on the footprint nearest the map
//            center (nearestOnFootprint)
//   null     no part of the footprint in the region: hidden
//
// nearestOnFootprint is exact, not sampled: the nearest point of a region to
// a point outside it lies on the region's boundary, which is made of pieces
// of the footprint's edges, the region's edges and the holes' edges. Each of
// those segments is cut where any other crosses it; a piece borders the
// region if a point just beside its middle is in it, and the piece's point
// nearest the center (nudged NUDGE_PX into the region) is a candidate.

/** Between the chip and any box, and the map's edge (px). */
export const CHIP_GAP_PX = 4;
const NUDGE_PX = 1e-4;

/**
 * @param {object} o
 * @param {number} o.width   the map container, px
 * @param {number} o.height
 * @param {{w: number, h: number}} o.chip
 * @param {{x0: number, y0: number, x1: number, y1: number}[]} [o.obstacles]  in the container's px
 * @param {[number, number]|null} [o.clipX]  the visible span in Compare
 * @returns {{x0: number, y0: number, x1: number, y1: number, holes: object[]}|null}
 *   where the chip's center may be; null when the chip fits nowhere
 */
export function chipRegion({ width, height, chip, obstacles = [], clipX = null }) {
  const hw = chip.w / 2 + CHIP_GAP_PX, hh = chip.h / 2 + CHIP_GAP_PX;
  const [l, r] = clipX ?? [0, width];
  const x0 = Math.max(0, l) + hw, x1 = Math.min(width, r) - hw;
  const y0 = hh, y1 = height - hh;
  if (!(x0 <= x1 && y0 <= y1)) return null;
  const holes = obstacles
    .filter((o) => o.x1 > o.x0 && o.y1 > o.y0)
    .map((o) => ({ x0: o.x0 - hw, y0: o.y0 - hh, x1: o.x1 + hw, y1: o.y1 + hh }));
  return { x0, y0, x1, y1, holes };
}

const inHole = (h, x, y) => x > h.x0 && x < h.x1 && y > h.y0 && y < h.y1;

/** The chip centered on p is wholly in the safe area. */
export function isFeasible(region, p) {
  if (!region || !p) return false;
  const { x, y } = p;
  if (!(x >= region.x0 && x <= region.x1 && y >= region.y0 && y <= region.y1)) return false;
  return !region.holes.some((h) => inHole(h, x, y));
}

/** Even-odd: p is inside the rings (shells and holes, of one or more polygons). */
export function pointInRings(rings, [x, y]) {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i], [xj, yj] = ring[j];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

/** The region's top-center, walked down past every hole across it; null if none is left. */
export function fixedAnchor(region) {
  if (!region) return null;
  const x = (region.x0 + region.x1) / 2;
  let y = region.y0;
  for (let n = 0; n <= region.holes.length; n++) {
    const h = region.holes.find((k) => inHole(k, x, y));
    if (!h) break;
    y = h.y1;
  }
  return isFeasible(region, { x, y }) ? { x, y } : null;
}

function rectEdges({ x0, y0, x1, y1 }) {
  return [[[x0, y0], [x1, y0]], [[x1, y0], [x1, y1]], [[x1, y1], [x0, y1]], [[x0, y1], [x0, y0]]];
}

// The parameter along a-b where it crosses c-d, or null.
function crossAt([a, b], [c, d]) {
  const rx = b[0] - a[0], ry = b[1] - a[1], sx = d[0] - c[0], sy = d[1] - c[1];
  const den = rx * sy - ry * sx;
  if (den === 0) return null;
  const t = ((c[0] - a[0]) * sy - (c[1] - a[1]) * sx) / den;
  const u = ((c[0] - a[0]) * ry - (c[1] - a[1]) * rx) / den;
  return t > 0 && t < 1 && u >= 0 && u <= 1 ? t : null;
}

/**
 * The point of the region on the footprint nearest `center`; null when the
 * footprint has no part in the region.
 */
export function nearestOnFootprint(rings, region, center) {
  if (!region) return null;
  const ok = (x, y) => isFeasible(region, { x, y }) && pointInRings(rings, [x, y]);
  const [cx, cy] = center;
  if (ok(cx, cy)) return { x: cx, y: cy };
  // Only segments that reach the region's rectangle can border it.
  const touches = ([a, b]) => Math.max(a[0], b[0]) >= region.x0 && Math.min(a[0], b[0]) <= region.x1
    && Math.max(a[1], b[1]) >= region.y0 && Math.min(a[1], b[1]) <= region.y1;
  const segs = [];
  for (const ring of rings) for (let i = 1; i < ring.length; i++) segs.push([ring[i - 1], ring[i]]);
  segs.push(...rectEdges(region));
  for (const h of region.holes) segs.push(...rectEdges(h));
  const live = segs.filter(touches);

  let best = null, bestD = Infinity;
  for (const s of live) {
    const [[ax, ay], [bx, by]] = s;
    const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy);
    if (len === 0) continue;
    const nx = -dy / len, ny = dx / len;
    const ts = [0, 1];
    for (const o of live) if (o !== s) { const t = crossAt(s, o); if (t !== null) ts.push(t); }
    ts.sort((p, q) => p - q);
    const tc = ((cx - ax) * dx + (cy - ay) * dy) / (len * len);
    for (let i = 1; i < ts.length; i++) {
      const ta = ts[i - 1], tb = ts[i];
      if (tb - ta < 1e-12) continue;
      const tm = (ta + tb) / 2, mx = ax + dx * tm, my = ay + dy * tm;
      const side = ok(mx + nx * NUDGE_PX, my + ny * NUDGE_PX) ? 1 : ok(mx - nx * NUDGE_PX, my - ny * NUDGE_PX) ? -1 : 0;
      if (!side) continue;
      const t = Math.min(tb, Math.max(ta, tc));
      let qx = ax + dx * t + side * nx * NUDGE_PX, qy = ay + dy * t + side * ny * NUDGE_PX;
      if (!ok(qx, qy)) { qx = mx + side * nx * NUDGE_PX; qy = my + side * ny * NUDGE_PX; }
      const d = (qx - cx) ** 2 + (qy - cy) ** 2;
      if (d < bestD) { bestD = d; best = { x: qx, y: qy }; }
    }
  }
  return best;
}

/**
 * @param {object} o
 * @param {number[][][]} o.rings        the footprint, projected (container px)
 * @param {[number, number]|null} o.labelPoint  its label_point, projected
 * @param {[number, number]} o.center   the map center, projected
 * @param {object|null} o.region        chipRegion's
 * @param {{x: number, y: number}|null} [o.current]  where the chip is now
 * @returns {{x: number, y: number, branch: "kept"|"label"|"anchor"|"nearest"}|null}
 */
export function placeChip({ rings, labelPoint, center, region, current = null }) {
  if (!region) return null;
  if (current && isFeasible(region, current) && pointInRings(rings, [current.x, current.y])) {
    return { x: current.x, y: current.y, branch: "kept" };
  }
  if (labelPoint && isFeasible(region, { x: labelPoint[0], y: labelPoint[1] })) {
    return { x: labelPoint[0], y: labelPoint[1], branch: "label" };
  }
  if (pointInRings(rings, center)) {
    const a = fixedAnchor(region);
    if (a) return { ...a, branch: "anchor" };
  }
  const n = nearestOnFootprint(rings, region, center);
  return n ? { ...n, branch: "nearest" } : null;
}
